import { Image, Pressable, StyleSheet, View } from 'react-native';
import { Camera, Image as ImageIcon, Upload, Zap } from 'lucide-react-native';
import type { PhotoSearchImageSource } from '../../types/photoSearch';
import { tokens } from '../../theme/tokens';
import { AppBadge } from '../ui/AppBadge';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppIconButton } from '../ui/AppIconButton';
import {
  AppSegmentedControl,
  type AppSegmentedControlOption,
} from '../ui/AppSegmentedControl';
import { AppText } from '../ui/AppText';

type CaptureSource = Extract<PhotoSearchImageSource, 'camera' | 'paper'>;

type PhotoSearchCaptureCardProps = {
  imageUri: string | null;
  activeSource: PhotoSearchImageSource;
  isSubmitting: boolean;
  onChangeSource: (source: PhotoSearchImageSource) => void;
  onPickImage: (source: PhotoSearchImageSource) => void;
  onSubmit: () => void;
  onToggleFlash: () => void;
};

const sourceOptions: AppSegmentedControlOption<CaptureSource>[] = [
  { id: 'camera', label: '搜题' },
  { id: 'paper', label: '试卷' },
];

const previewGuides = ['题干完整入框', '保持纸面平整', '光线充足'];

export function PhotoSearchCaptureCard({
  imageUri,
  activeSource,
  isSubmitting,
  onChangeSource,
  onPickImage,
  onSubmit,
  onToggleFlash,
}: PhotoSearchCaptureCardProps) {
  const captureSource: CaptureSource = activeSource === 'paper' ? 'paper' : 'camera';
  const previewModeLabel = captureSource === 'paper' ? '试卷预览' : '题目预览';
  const submitLabel = captureSource === 'paper' ? '提交试卷' : '提交搜题';

  return (
    <AppCard style={styles.card} density="compact">
      <View style={styles.hero}>
        <View style={styles.heroIcon}>
          <Camera size={26} color={tokens.colors.primary} />
        </View>
        <View style={styles.heroCopy}>
          <AppText variant="title" weight="bold">
            拍照搜题
          </AppText>
          <AppText variant="body" tone="muted" style={styles.heroText}>
            对齐题目边缘后上传，AI 会先识别题干，再进入讲解或候选确认。
          </AppText>
        </View>
      </View>

      <AppSegmentedControl
        options={sourceOptions}
        value={captureSource}
        onChange={onChangeSource}
        style={styles.modeCapsule}
      />

      <View style={[styles.previewBox, styles.largePreview, styles.viewfinder]}>
        {imageUri ? (
          <Image source={{ uri: imageUri }} style={styles.previewImage} resizeMode="cover" />
        ) : (
          <View style={styles.emptyPreview}>
            <ImageIcon size={44} color={tokens.colors.primary} />
            <AppText variant="body" weight="bold" style={styles.emptyTitle}>
              等待题图
            </AppText>
            <AppText variant="caption" tone="muted">
              拍照或从相册选择一张清晰题目
            </AppText>
          </View>
        )}

        <View pointerEvents="none" style={styles.gridOverlay}>
          <View style={[styles.gridLine, styles.gridVerticalOne]} />
          <View style={[styles.gridLine, styles.gridVerticalTwo]} />
          <View style={[styles.gridLine, styles.gridHorizontalOne]} />
          <View style={[styles.gridLine, styles.gridHorizontalTwo]} />
        </View>

        <View style={styles.previewMetaBar}>
          <AppBadge label={previewModeLabel} tone="brand" />
          <AppIconButton
            accessibilityLabel="切换闪光灯"
            icon={<Zap size={18} color={tokens.colors.white} />}
            variant="solid"
            size="compact"
            onPress={onToggleFlash}
            style={styles.flashButton}
          />
        </View>

        <View style={styles.previewGuideBar}>
          {previewGuides.map((guide) => (
            <AppBadge key={guide} label={guide} tone="neutral" />
          ))}
        </View>
      </View>

      <View style={styles.cameraControls}>
        <Pressable
          accessibilityRole="button"
          onPress={() => onPickImage('album')}
          style={styles.albumTile}
        >
          <ImageIcon size={22} color={tokens.colors.primary} />
          <AppText variant="caption" tone="primary" weight="bold">
            相册
          </AppText>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          disabled={isSubmitting}
          onPress={() => onPickImage(captureSource)}
          style={[styles.shutterButton, isSubmitting ? styles.disabledControl : null]}
        >
          <View style={styles.shutterInner} />
        </Pressable>

        <Pressable accessibilityRole="button" onPress={onToggleFlash} style={styles.albumTile}>
          <Zap size={22} color={tokens.colors.primary} />
          <AppText variant="caption" tone="primary" weight="bold">
            闪光
          </AppText>
        </Pressable>
      </View>

      <AppButton
        label={submitLabel}
        onPress={onSubmit}
        loading={isSubmitting}
        disabled={!imageUri}
        size="lg"
        leftSlot={<Upload size={18} color={tokens.colors.white} />}
      />
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.lg,
  },
  hero: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  heroIcon: {
    width: 58,
    height: 58,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  heroCopy: {
    flex: 1,
    gap: 2,
  },
  heroText: {
    maxWidth: 340,
  },
  modeCapsule: {
    alignSelf: 'stretch',
  },
  previewBox: {
    minHeight: tokens.component.photoSearchPreview.minHeight,
    borderRadius: tokens.component.photoSearchPreview.radius,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    backgroundColor: tokens.colors.background,
    overflow: 'hidden',
    position: 'relative',
  },
  largePreview: {
    ...tokens.shadow.card,
  },
  viewfinder: {
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: tokens.component.photoSearchPreview.mediaHeight,
  },
  emptyPreview: {
    minHeight: tokens.component.photoSearchPreview.mediaHeight,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.lg,
  },
  emptyTitle: {
    marginTop: tokens.spacing.xs,
  },
  gridOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
  gridLine: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,0.58)',
  },
  gridVerticalOne: {
    top: 0,
    bottom: 0,
    left: '33%',
    width: 1,
  },
  gridVerticalTwo: {
    top: 0,
    bottom: 0,
    left: '66%',
    width: 1,
  },
  gridHorizontalOne: {
    left: 0,
    right: 0,
    top: '33%',
    height: 1,
  },
  gridHorizontalTwo: {
    left: 0,
    right: 0,
    top: '66%',
    height: 1,
  },
  previewMetaBar: {
    position: 'absolute',
    top: tokens.spacing.md,
    left: tokens.spacing.md,
    right: tokens.spacing.md,
    zIndex: 2,
    minHeight: tokens.component.photoSearchPreview.controlHeight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
  },
  flashButton: {
    backgroundColor: 'rgba(43,33,24,0.64)',
  },
  previewGuideBar: {
    position: 'absolute',
    left: tokens.spacing.md,
    right: tokens.spacing.md,
    bottom: tokens.spacing.md,
    zIndex: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.xs,
  },
  cameraControls: {
    minHeight: 78,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  albumTile: {
    width: 72,
    height: 62,
    borderRadius: tokens.radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  shutterButton: {
    width: 72,
    height: 72,
    borderRadius: tokens.radius.full,
    borderWidth: 4,
    borderColor: tokens.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.white,
  },
  shutterInner: {
    width: 52,
    height: 52,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.colors.primary,
  },
  disabledControl: {
    opacity: 0.56,
  },
});
